import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchCircleMemberships } from "../../../store/actions/circleActions";
import { fetchCoSignQueue, guardianCoSign } from "../../ForAthletes/v2/athleteServices";
import { Avatar, Badge, BmModal, Btn, Card, EmptyState, ErrorState, Loading, PageHeader } from "../../ForBrands/ui/BrandUI";

// Supporter · My Circle, the home of a parent, guardian or trainer account:
// the minors' contracts waiting on their co-signature, and the athletes who
// added them to their Circle with what each one lets them do on the
// profile. The same data the old /circle page showed, in the V2 style.
// Also the /circle page of the business roles (eyebrow "Circle"): the editor
// opens in the shell the page sits in.

const PERMISSION_LABELS = { view: "View only", manage: "Manage", edit: "Edit" };
// The profile action a Circle permission opens ("view" opens none).
const EDIT_LABELS = { edit: "Edit profile", manage: "Add details" };
const LOAD_FAILED = "We could not load your Circle. Try again.";

const dealTitle = (deal) => [deal.campaignName || "NIL campaign", deal.brand?.company].filter(Boolean).join(" · ");
const dealTerms = (deal) => [
  [deal.athlete?.name, deal.athlete?.surname].filter(Boolean).join(" "),
  `$${Number(deal.offer?.compensation || 0).toLocaleString()}`,
  (deal.offer?.deliverableLines || []).join("; ") || "see contract",
].filter(Boolean).join(" · ");

// The co-sign queue is extra: the memberships still show when it fails.
const loadCoSignQueue = async () => {
  try {
    return (await fetchCoSignQueue()).deals || [];
  } catch {
    return [];
  }
};

// The athlete editor beside this page: the supporter shell's, or /circle's.
export const circleEditorPath = (pathname) => (String(pathname || "").startsWith("/supporter") ? "/supporter/athlete/edit" : "/circle/athlete/edit");

const SupporterCirclePage = ({ eyebrow = "Supporter" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const email = useSelector((state) => state.user?.user?.email || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [memberships, deals] = await Promise.all([dispatch(fetchCircleMemberships()), loadCoSignQueue()]);
      setData({ memberships: memberships || [], deals });
      setError("");
    } catch {
      setError(LOAD_FAILED);
    }
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const coSign = async () => {
    setSigning(true);
    try {
      await guardianCoSign(confirming.id);
      toast.success("Contract co-signed.");
      setConfirming(null);
      await load();
    } catch (err) {
      toast.error(err?.data?.message || "We could not co-sign the contract. Try again.");
    } finally {
      setSigning(false);
    }
  };

  const openEditor = (membership) => {
    localStorage.setItem("circleAthleteEmail", membership.athleteEmail);
    navigate(circleEditorPath(pathname));
  };

  const header = (
    <PageHeader eyebrow={eyebrow} title="My Circle" subtitle="The athletes who added you to their Circle, and what you can do on their profile." />
  );
  if (error && !data) {
    return (
      <div>
        {header}
        <div className="flex flex-col items-start gap-3">
          <ErrorState>{error}</ErrorState>
          <Btn variant="secondary" onClick={load}>Try again</Btn>
        </div>
      </div>
    );
  }
  if (!data) return <Loading />;

  return (
    <div>
      {header}
      <div className="flex flex-col gap-6">
        {data.deals.length > 0 && (
          <section className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="bm-h-s text-bm-ink">Contracts awaiting your co-signature</h2>
              <p className="bm-body-m text-bm-muted">Athletes under 18 need a parent or guardian to co-sign. Review the terms, then co-sign to activate the deal.</p>
            </div>
            <Card padding="p-0" className="divide-y divide-bm-border overflow-hidden">
              {data.deals.map((deal) => (
                <div key={deal.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="bm-h-xs text-bm-ink">{dealTitle(deal)}</span>
                    <span className="bm-body-s text-bm-muted">{dealTerms(deal)}</span>
                  </div>
                  <Btn size="sm" onClick={() => setConfirming(deal)}>Co-sign contract</Btn>
                </div>
              ))}
            </Card>
          </section>
        )}

        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="bm-h-s text-bm-ink">Athletes you support</h2>
            <p className="bm-body-m text-bm-muted">
              An athlete adds you from My Circle in their CACHE account{email ? ` with your email, ${email}` : ""}.
            </p>
          </div>
          {data.memberships.length === 0 ? (
            <EmptyState>No athletes yet. When an athlete adds you to their Circle, they show up here.</EmptyState>
          ) : (
            <Card padding="p-0" className="divide-y divide-bm-border overflow-hidden">
              {data.memberships.map((m) => (
                <div key={m.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <Avatar src={m.avatar} name={m.athleteName || m.athleteEmail} size={40} tone="outline" />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="truncate bm-h-xs text-bm-ink">{m.athleteName || m.athleteEmail}</span>
                    <span className="flex flex-wrap gap-2">
                      {m.relationship && <Badge tone="soft">Their {m.relationship}</Badge>}
                      <Badge tone="outline">{PERMISSION_LABELS[m.permission] || m.permission}</Badge>
                    </span>
                  </div>
                  {m.athleteId && <Btn variant="secondary" size="sm" onClick={() => navigate(`/p/${m.athleteId}`)}>View profile</Btn>}
                  {EDIT_LABELS[m.permission] && <Btn size="sm" onClick={() => openEditor(m)}>{EDIT_LABELS[m.permission]}</Btn>}
                </div>
              ))}
            </Card>
          )}
        </section>
      </div>

      {confirming && (
        <BmModal
          title="Co-sign contract"
          onClose={() => !signing && setConfirming(null)}
          width={480}
          footer={
            <>
              <Btn onClick={coSign} disabled={signing}>{signing ? "Signing..." : "Co-sign contract"}</Btn>
              <Btn variant="secondary" onClick={() => setConfirming(null)} disabled={signing}>Cancel</Btn>
            </>
          }
        >
          <p className="bm-body-l text-bm-ink">
            Co-sign the {confirming.campaignName || "NIL"} contract for {[confirming.athlete?.name, confirming.athlete?.surname].filter(Boolean).join(" ") || "this athlete"}?
          </p>
          <p className="mt-3 bm-body-m text-bm-muted">{dealTerms(confirming)}</p>
        </BmModal>
      )}
    </div>
  );
};

export default SupporterCirclePage;
